import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { useNavigate } from '@remix-run/react';
import type { TButtonElement } from '@shared/dream-components';
import { ButtonElement } from '@shared/dream-components';
import { produce } from 'immer';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import WebinarReservationInfo from '~/components/WebinarReservationInfo';
import WebinarSeatsInfo from '~/components/WebinarSeatsInfo';
import { usePageContext } from '~/context/usePageContext';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useProductWebinarReservation from '~/hooks/useLoaders/useProductWebinarReservation';
import useWebinarDetails from '~/hooks/useLoaders/useWebinarDetails';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { ProductLoaderData } from '~/routes/products/$slug';
import { isProductActionDisabled } from '~/utils/getProductVariantWebinarDetails';

import { useModalContext } from '../context/useModalContext';

import useProductMergeTagProcessor from '../hooks/useProductMergeTagProcessor';

import BuyNowAction from './BuyNowAction';

interface Props {
  node: TButtonElement;
  children: ReactNode | ReactNode[];
}

const Button = ({ node, children }: Props) => {
  const navigate = useNavigate();
  const { handleOpenModal, handleCloseModal } = useGlobalModals();
  const { subscription, setCloseModalActionDispatched, openPageModalType } = usePageContext();
  const { product } = useTypedLoaderData<ProductLoaderData>();
  const modalContext = useModalContext();
  const isLoggedIn = useIsLoggedIn();

  const { children: productButtonChildren } = useProductMergeTagProcessor({ node, children });

  const mergeTag = node?.attrs?.mergeTag;
  const onClick = node?.attrs && 'onClick' in node.attrs ? node.attrs.onClick : undefined;
  const action = node?.attrs && 'action' in node.attrs && node.attrs.action;
  const currentHref = node.attrs?.url || '';
  const isOpenAction = onClick === 'action' || (onClick === undefined && !!action);
  let url: URL | undefined;
  let handleClientSideNavigation: (() => void) | undefined;
  let isClientSideNavigation = false;

  const isTelLink = currentHref.startsWith('tel:');

  try {
    if (!isTelLink) {
      url = new URL(currentHref);
    }
  } catch {
    // Fail silently
  }

  // On actions we only want to
  if (isOpenAction) {
    if (typeof window !== 'undefined') {
      url = new URL(window.location.href);
    }

    if (action === 'login' && url) {
      url.searchParams.set('modal', 'login');
    } else if (action === 'sign_up' && url) {
      url.searchParams.set('modal', 'signup');
    } else if (action === 'profile' && url) {
      url.pathname = `subscribe/${subscription?.subscriber_id || ''}/manage`;
    } else if (action === 'search' && url) {
      url.searchParams.set('modal', 'search');
    } else if (action === 'close_modal' && url) {
      url.searchParams.delete('modal');
    }
  }

  // Ensure the button is not open in a new tab if it has an action
  const nodeWithModifiedAttrs = useMemo(
    () =>
      produce(node, (draft) => {
        if (draft.attrs && isOpenAction && url) {
          draft.attrs.open_in_new_tab = false;
        }
      }),
    [node, isOpenAction, url],
  );

  if (
    !nodeWithModifiedAttrs.attrs?.open_in_new_tab &&
    url &&
    !nodeWithModifiedAttrs.attrs?.isCustomLink &&
    !isTelLink
  ) {
    handleClientSideNavigation = () => {
      if (typeof action === 'string' && ['sign_up', 'search', 'login', 'close_modal'].includes(action)) {
        switch (action) {
          case 'search':
          case 'login':
            handleOpenModal(action, { preventScrollReset: true });
            break;
          case 'sign_up':
            handleOpenModal('signup', { preventScrollReset: true });
            break;
          case 'close_modal':
            // Use modal context onClose if available and it's an email capture modal
            if (modalContext?.onClose) {
              modalContext.onClose();
            } else {
              handleCloseModal({ preventScrollReset: true });
              if (openPageModalType) {
                setCloseModalActionDispatched(true);
              }
            }
            break;
          default:
            navigate(`${url.pathname}${url.search}${url.hash}`);
        }

        return;
      }
      navigate(`${url.pathname}${url.search}${url.hash}`);
    };
    isClientSideNavigation = true;
  }

  // This is a special case for the product buy action
  // We intercept the button and render a different component to ensure the payment flow is handled correctly
  const isBuyAction = mergeTag === 'product.buy_action' && !!product;
  const webinarRedeemable = product?.default_product_variant?.redeemable;
  const isWebinar = webinarRedeemable?.type === 'ProductRedeemables::Webinar';

  const { data: webinarDetails } = useWebinarDetails({
    slug: product?.slug ?? '',
    enabled: isBuyAction && isWebinar,
  });

  const { data: webinarReservation } = useProductWebinarReservation({
    slug: product?.slug ?? '',
    enabled: isBuyAction && isWebinar && isLoggedIn,
  });

  if (isBuyAction) {
    const reservationRedirectUrl = webinarReservation?.redirect_url ?? null;
    const hasActiveReservation = !!reservationRedirectUrl;
    const isDisabled = isProductActionDisabled(product, webinarRedeemable?.status) || hasActiveReservation;

    const freshRemainingSeats = webinarDetails?.remaining_seats ?? webinarRedeemable?.remaining_seats;
    const freshMaxSeats = webinarDetails?.max_seats ?? webinarRedeemable?.max_seats;

    return (
      <>
        <BuyNowAction product={product} reservationRedirectUrl={reservationRedirectUrl}>
          {({ isLoading }) => (
            <ButtonElement
              type="submit"
              isClientSideNavigation={true}
              element={nodeWithModifiedAttrs}
              disabled={isLoading || isDisabled}
            >
              {productButtonChildren}
            </ButtonElement>
          )}
        </BuyNowAction>
        {hasActiveReservation && reservationRedirectUrl && (
          <WebinarReservationInfo
            redirectUrl={reservationRedirectUrl}
            eventStartsAt={webinarDetails?.event_starts_at ?? webinarRedeemable?.event_starts_at}
            eventEndsAt={webinarDetails?.event_ends_at ?? webinarRedeemable?.event_ends_at}
            status={webinarRedeemable?.status}
            className="-mt-3.5"
          />
        )}
        {!isDisabled && (
          <WebinarSeatsInfo remainingSeats={freshRemainingSeats} maxSeats={freshMaxSeats} className="-mt-3.5" />
        )}
      </>
    );
  }

  return (
    <ButtonElement
      element={nodeWithModifiedAttrs}
      onClick={handleClientSideNavigation}
      isClientSideNavigation={isClientSideNavigation}
    >
      {children}
    </ButtonElement>
  );
};

export default Button;
